/*
PURPOSE: The pure branching and formatting behind the contribution fee breakdown, kept out of
the component so it can be tested directly rather than through a render.

USE CASES:
- Deciding whether a record carries enough detail to show the cost-transparency layer
- Deriving the displayed total from the components actually on screen
- Formatting integer cents for display

DOMAIN BOUNDARIES:
- Pure. No React, no fetching, and above all no fee arithmetic: the split is produced once by
  the shared contribution-fee producer and rendered verbatim from here.

ASSUMPTIONS:
- Money arrives as integer cents
- Optional cost-layer fields are PRESENCE-gated, not truthiness-gated: a stored zero is a real
  figure and renders, while a genuinely absent field renders nothing rather than a false $0.00
*/

/**
 * The minimal shape this breakdown needs. Receipt fields are required; the cost-transparency
 * fields are optional so a record without them renders the receipt alone.
 */
export interface ContributionBreakdownView {
  /** What the member paid. */
  totalCents: number;
  /** What reaches the artists. */
  artistShareCents: number;
  /** The Platform Fee: the whole retained amount, GST-inclusive. */
  retainedCents: number;
  /** Cost-transparency layer — wearecoral's costs, not a charge to the member. */
  processingCents?: number;
  gstCents?: number;
  coralShareCents?: number;
}

/** A breakdown that carries the cost layer, so its figures need no non-null assertion. */
export type BreakdownWithCosts = ContributionBreakdownView &
  Required<Pick<ContributionBreakdownView, 'processingCents' | 'gstCents' | 'coralShareCents'>>;

/**
 * True when every cost-layer figure is present. Presence-gated (`!= null`), so an explicitly
 * stored 0 still counts and a missing field does not become a fabricated zero.
 */
export function hasCostDetail(b: ContributionBreakdownView): b is BreakdownWithCosts {
  return b.processingCents != null && b.gstCents != null && b.coralShareCents != null;
}

/**
 * The total to display, derived as the sum of the components actually shown rather than read
 * independently. A separately-derived total can visibly disagree with the lines above it, which
 * is precisely the failure this avoids — the figures on screen must add up to the figure on screen.
 */
export function displayedTotalCents(b: ContributionBreakdownView): number {
  return b.artistShareCents + b.retainedCents;
}

/**
 * The Coral share as a whole-number percentage of the contribution, or null when the figure is
 * absent. Taken against the DISPLAYED total, not the fee: a percentage stated inside the cost
 * block would otherwise be read against the fee, where the same amount is several times larger.
 */
export function coralSharePercentOfTotal(b: ContributionBreakdownView): number | null {
  const totalCents = displayedTotalCents(b);
  if (b.coralShareCents == null || totalCents <= 0) return null;
  return Math.round((b.coralShareCents / totalCents) * 100);
}

/** Integer cents to a plain dollar string, matching how the surrounding surfaces render money. */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

/**
 * Build a breakdown for DISPLAY without letting a reconciliation failure take the page down.
 *
 * The producers fail loud by design, which is right on the settlement path. On a member-facing
 * surface it is not: these run inside render, on account pages that have no error boundary, so
 * an unbalanced stored record would blank the whole page rather than degrade. A stored record
 * CAN be unbalanced — the settlement layer has a preserved defect where a processing cost at or
 * above the pledge yields negative components.
 *
 * Returns null instead, so the caller renders nothing. The failure is still surfaced, loudly and
 * with a stable token, rather than swallowed.
 */
export function safeBreakdown<T>(build: () => T): T | null {
  try {
    return build();
  } catch (error) {
    // Stable token: a money reconciliation failing silently is the thing being guarded against.
    // eslint-disable-next-line no-console
    console.error('CONTRIBUTION_BREAKDOWN_UNBALANCED', error);
    return null;
  }
}
